type StorageType = 'localStorage' | 'sessionStorage' | 'memory';

class MemoryStorage {
  private storage: Map<string, string> = new Map();

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

const memoryStorage = new MemoryStorage();

function isStorageAvailable(type: StorageType): boolean {
  if (type === 'memory') return true;
  
  try {
    const storage = type === 'localStorage' ? window.localStorage : window.sessionStorage;
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return false;
  }
}

class SafeStorage {
  private type: StorageType;
  private storage: Storage | MemoryStorage;

  constructor(preferredType: StorageType = 'localStorage') {
    if (isStorageAvailable(preferredType)) {
      this.type = preferredType;
      this.storage = preferredType === 'localStorage' ? window.localStorage : 
                     preferredType === 'sessionStorage' ? window.sessionStorage : 
                     memoryStorage;
    } else if (isStorageAvailable('sessionStorage')) {
      this.type = 'sessionStorage';
      this.storage = window.sessionStorage;
    } else {
      this.type = 'memory';
      this.storage = memoryStorage;
    }

    console.log(`Using ${this.type} for storage`);
  }

  getItem(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      console.warn(`Error getting item from ${this.type}:`, error);
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.storage.setItem(key, value);
    } catch (error) {
      console.warn(`Error setting item in ${this.type}:`, error);
      // If quota exceeded, try clearing old items
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.clearOldItems();
        try {
          this.storage.setItem(key, value);
        } catch (retryError) {
          console.error(`Failed to set item even after clearing storage:`, retryError);
        }
      }
    }
  }

  removeItem(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.warn(`Error removing item from ${this.type}:`, error);
    }
  }

  private clearOldItems(): void {
    try {
      if (this.type === 'memory') {
        (this.storage as MemoryStorage).clear();
      } else {
        const storage = this.storage as Storage;
        const keys = Object.keys(storage);
        keys.forEach(key => {
          if (key.startsWith('_cached_')) {
            storage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.warn(`Error clearing old items from ${this.type}:`, error);
    }
  }
}

export const safeStorage = new SafeStorage('localStorage');
